// Shared building blocks for the guest messaging screens. They follow the
// current Teache look: Poppins, near-black pill buttons, lavender-bordered
// inputs, the indigo accent and warm neutral surfaces.
import {useState} from "react";
import styled, {css, keyframes} from "styled-components";
import {defaultThemeColors as c} from "ui/theme";

export const tokens = {
    ink: c.basicBlack, // #1D1D1F: headings, primary buttons
    text: c.txt, // #191D3A
    muted: c.lightGrey, // #757688
    placeholder: c.placeholder,
    border: c.mobileInputBorder, // #E2E1F1
    borderSoft: c.lightBlue, // #EDF0FF
    accent: c.blue, // #554DF1
    accentSoft: "rgba(85, 77, 241, 0.08)",
    warm: c.secondary, // #F7F5F1
    error: c.red,
    errorSoft: "#FDECEC",
    warningSoft: "#FFF6E6",
};

const spin = keyframes`to { transform: rotate(1turn); }`;

export const Spinner = styled.span`
    display: inline-block;
    width: ${({$size = 18}) => $size}px;
    height: ${({$size = 18}) => $size}px;
    border-radius: 50%;
    border: 2px solid ${({$light}) => ($light ? "rgba(255,255,255,0.35)" : tokens.borderSoft)};
    border-top-color: ${({$light}) => ($light ? "#fff" : tokens.accent)};
    animation: ${spin} 0.8s linear infinite;
    flex-shrink: 0;
`;

const pillBase = css`
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 48px;
    padding: 0 28px;
    border-radius: 100px;
    font-family: var(--font-primary);
    font-size: 15px;
    font-weight: 500;
    line-height: 1;
    cursor: pointer;
    text-align: center;
    transition: background 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
    ${({$block}) => $block && "width: 100%;"}

    &:focus-visible {
        outline: 2px solid ${tokens.accent};
        outline-offset: 3px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`;

/** Primary action: near-black pill, like "Book Class" / "Show more dates". */
export const PillButton = styled.button`
    ${pillBase};
    background: ${tokens.ink};
    color: #fff;

    &:hover:not(:disabled) {
        background: #000;
    }
`;

/** Secondary action: outlined pill, like "Send a message". */
export const PillButtonOutline = styled.button`
    ${pillBase};
    background: transparent;
    color: ${tokens.ink};
    border: 2px solid ${tokens.ink};

    &:hover:not(:disabled) {
        background: rgba(29, 29, 31, 0.04);
    }
`;

export const PillLink = styled.a`
    ${pillBase};
    background: transparent;
    color: ${tokens.ink};
    border: 2px solid ${tokens.ink};
    text-decoration: none;
    min-height: 40px;
    padding: 0 18px;
    font-size: 14px;

    &:hover {
        background: rgba(29, 29, 31, 0.04);
    }
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

export const Label = styled.label`
    font-family: var(--font-primary);
    font-size: 14px;
    font-weight: 500;
    color: ${tokens.ink};
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
`;

export const LabelHint = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({$warn}) => ($warn ? tokens.error : tokens.muted)};
`;

const inputBase = css`
    box-sizing: border-box;
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${({"aria-invalid": invalid}) => (invalid ? tokens.error : tokens.border)};
    background: #fff;
    font-family: var(--font-primary);
    font-size: 16px; /* 16px stops iOS zooming into the field */
    color: ${tokens.text};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
        color: ${tokens.placeholder};
    }

    &:focus {
        outline: none;
        border-color: ${tokens.accent};
        box-shadow: 0 0 0 3px ${tokens.accentSoft};
    }
`;

export const Input = styled.input`
    ${inputBase};
    height: 50px;
    padding: 0 14px;
`;

export const TextArea = styled.textarea`
    ${inputBase};
    min-height: ${({$minHeight = 140}) => $minHeight}px;
    padding: 12px 14px;
    line-height: 1.5;
    resize: vertical;
`;

export const FieldError = styled.span`
    font-family: var(--font-primary);
    font-size: 13px;
    color: ${tokens.error};
`;

export const Alert = styled.div`
    box-sizing: border-box;
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px 14px;
    border-radius: 10px;
    font-family: var(--font-primary);
    font-size: 14px;
    line-height: 1.45;
    color: ${tokens.text};
    background: ${({$tone}) => ($tone === "warning" ? tokens.warningSoft : tokens.errorSoft)};

    svg {
        flex-shrink: 0;
        margin-top: 1px;
        color: ${({$tone}) => ($tone === "warning" ? "#B7791F" : tokens.error)};
    }
`;

export const Muted = styled.p`
    margin: 0;
    font-family: var(--font-primary);
    font-size: 13px;
    line-height: 1.5;
    color: ${tokens.muted};
    display: flex;
    gap: 8px;
    align-items: flex-start;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

/** Round avatar: the instructor's photo, or their initial on a soft tint. */
export const Avatar = ({src, name = "", size = 48}) => {
    const [failed, setFailed] = useState(false);
    const initial = (name.trim()[0] || "?").toUpperCase();
    return src && !failed ? (
        <AvatarImg src={src} alt="" $size={size} onError={() => setFailed(true)}/>
    ) : (
        <AvatarInitial $size={size} aria-hidden="true">{initial}</AvatarInitial>
    );
};

const AvatarImg = styled.img`
    width: ${({$size}) => $size}px;
    height: ${({$size}) => $size}px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: ${tokens.borderSoft};
`;

const AvatarInitial = styled.span`
    width: ${({$size}) => $size}px;
    height: ${({$size}) => $size}px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${tokens.accentSoft};
    color: ${tokens.accent};
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: ${({$size}) => Math.round($size * 0.4)}px;
`;

/** Large round icon used at the top of confirmation and status screens. */
export const IconBadge = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({$tone}) => ($tone === "neutral" ? tokens.warm : tokens.accentSoft)};
    color: ${({$tone}) => ($tone === "neutral" ? tokens.ink : tokens.accent)};
    flex-shrink: 0;
`;

const Svg = ({size = 18, children, strokeWidth = 2}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

export const LockIcon = (p) => (
    <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></Svg>
);
export const CheckIcon = (p) => <Svg {...p}><path d="M20 6 9 17l-5-5"/></Svg>;
export const SendIcon = (p) => <Svg {...p}><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4 20-7z"/></Svg>;
export const MailIcon = (p) => (
    <Svg {...p}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></Svg>
);
export const ClockIcon = (p) => <Svg {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></Svg>;
export const AlertIcon = (p) => (
    <Svg {...p}><circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/></Svg>
);
export const ChevronLeftIcon = (p) => <Svg {...p}><path d="m15 18-6-6 6-6"/></Svg>;
export const ChatIcon = (p) => (
    <Svg {...p}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/></Svg>
);
